/**
 * Sample data: whether this tab is exploring the product on the showcase
 * account (a fictional game, docs/DEMO_SHOWCASE.md) instead of the user's own.
 *
 * While it is on, every request carries `X-Lemon-Sample: 1` (src/api/client.ts).
 * The API answers the product's data reads from the showcase account, keeps
 * everything else (identity, account, billing, connections) the user's own,
 * and refuses writes (apps/api/api/auth.py, "Sample data").
 *
 * Per tab, in sessionStorage: a new tab, or a new visit, starts on the user's
 * own data. Turn it on and off through `useSampleMode` (features/sample), which
 * also resets the query cache so sample and real answers never mix.
 */
import { useSyncExternalStore } from 'react';
import { readStored, removeStored, writeStored } from './storage';

export const SAMPLE_HEADER = 'X-Lemon-Sample';
/** The showcase game's name, as the sample banner and tour say it. */
export const SAMPLE_GAME = 'Realmforge';

const KEY = 'lemon.sample';
const listeners = new Set<() => void>();
let active = readStored(KEY, 'session') === '1';

export const isSampleActive = (): boolean => active;

export function setSampleActive(next: boolean): void {
  if (next === active) return;
  active = next;
  if (next) writeStored(KEY, '1', 'session');
  else removeStored(KEY, 'session');
  for (const listener of listeners) listener();
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

export const useSampleActive = (): boolean => useSyncExternalStore(subscribe, isSampleActive, isSampleActive);
